import { useEffect, useState } from 'react';
import { Button, Dialog, InlineError } from '../../components/ui';
import { useAuth } from '../auth/AuthProvider';
import { useProspectingAvailability } from './useProspectingAvailability';
import { JourneyOperationDialog } from './JourneyOperationDialog';
import type { JourneyIntent } from './journeyOperations';
import { listPendingJourneys } from './journeyPending';

/** Available even when a completed cleanup/merge removed its original opener.
 * Storage is only a retry transport. The RPC checks current access on every call. */
export function JourneyPendingActions() {
  const { access } = useAuth();
  const { operations_enabled } = useProspectingAvailability();
  const actor = access.status === 'approved' ? access.profile.id : '';
  // Remount the whole recovery UI, not just its inner dialog, on account change.
  return operations_enabled ? <ActorPendingActions key={actor} actor={actor} /> : null;
}

function ActorPendingActions({ actor }: { actor: string }) {
  const [entries, setEntries] = useState<ReturnType<typeof listPendingJourneys>>([]);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<JourneyIntent[] | null>(null);
  useEffect(() => {
    function refresh() {
      try {
        setEntries(actor ? listPendingJourneys(actor) : []);
        setFailed(false);
      } catch {
        setFailed(true);
      }
    }
    refresh();
    window.addEventListener('tap-journey-pending-changed', refresh);
    return () => window.removeEventListener('tap-journey-pending-changed', refresh);
  }, [actor]);
  if (!actor || (!entries.length && !failed)) return null;
  return (
    <>
      <Button variant="ghost" onClick={() => setOpen(true)}>
        Unfinished actions{entries.length ? ` (${entries.length})` : ''}
      </Button>
      {open && selected === null && (
        <Dialog open title="Unfinished actions" onClose={() => setOpen(false)}>
          <p>Check the same saved requests, even if their records no longer appear in the list.</p>
          {failed && (
            <InlineError>
              Saved retry details could not be read. Do not start a replacement action; ask an
              admin.
            </InlineError>
          )}
          {entries.map((entry) => (
            <p key={entry.key}>
              <Button variant="ghost" onClick={() => setSelected(entry.intents)}>
                {entry.intents.map((intent) => intent.label).join('; ')}
              </Button>
            </p>
          ))}
        </Dialog>
      )}
      {open && selected !== null && (
        <JourneyOperationDialog
          key={actor}
          intents={selected}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null);
            setOpen(false);
          }}
        />
      )}
    </>
  );
}
